import { BuildClass, Do, fsmData } from '../../universal';
import { React, _ } from '../lib';
import { J2rModalPortal, J2rText, j2r } from './component-react';
import { ConditionalObject } from './ui5';
const MAX_AUTOCOMPLETE_SHOWN = 20;
export class J2rTextAutoComplete extends React.Component {
    element;
    callback;
    fetching;
    constructor(props) {
        super(props);
        this.element = React.createRef();
        this.state = {
            value: this.props.value ?? '',
            focused: false,
            closed: false,
            highlightedIndex: -1,
            cachedMatches: [],
        };
    }
    componentDidMount() {
        this.getMatchingOptions(x => {
            this.setState({ cachedMatches: x });
        });
    }
    componentDidUpdate(prevProps, prevState) {
        const cause_for_change = _.some([
            !_.isEqual(prevProps, this.props),
            this.state.value !== prevState.value,
        ]);
        if (cause_for_change) {
            this.getMatchingOptions(x => {
                this.setState({ cachedMatches: x });
            });
        }
    }
    render() {
        return j2r({
            cl: 'j2rautocomplete',
            children: [this.buildInput(), this.buildOptions()],
        });
    }
    buildInput() {
        return {
            tag: J2rText,
            key: 'input',
            ref: this.element,
            autoComplete: 'off',
            value: this.state.value,
            onKeyDown: e => {
                switch (e.which) {
                    case 27:
                        e.stopPropagation();
                        e.preventDefault();
                        this.setState({ closed: true });
                        break;
                    case 38:
                        this.moveSelection(e, -1);
                        break;
                    case 40:
                        this.moveSelection(e, 1);
                        break;
                    case 13:
                        this.confirmSelection(true);
                        break;
                    case 9:
                        this.confirmSelection(false);
                        break;
                }
            },
            onFocus: () => {
                this.setState({
                    focused: true,
                    closed: false,
                });
            },
            onBlur: () => {
                this.setState({
                    focused: false,
                    closed: false,
                    highlightedIndex: -1,
                });
            },
            onUpdate: v => {
                this.setState({
                    value: v,
                    closed: false,
                    highlightedIndex: -1,
                }, () => {
                    this.updateValue(v, null);
                });
            },
        };
    }
    willShowOptions() {
        return _.every([
            this.state.focused,
            !this.state.closed,
            this.state.cachedMatches.length > 0,
        ]);
    }
    buildOptions() {
        return {
            tag: J2rModalPortal,
            key: 'modal-options-outer',
            children: [
                {
                    key: 'options',
                    cl: BuildClass({
                        'autocomplete-options': true,
                        closed: !this.willShowOptions(),
                    }),
                    style: Do(() => {
                        const el = this.element.current?.element.current;
                        const box = el?.getBoundingClientRect() ?? {
                            bottom: 0,
                            left: 0,
                            width: 0,
                        };
                        return {
                            top: `${box.bottom - 1}px`,
                            left: `${box.left}px`,
                            minWidth: `${box.width}px`,
                        };
                    }),
                    children: Do(() => {
                        if (!this.willShowOptions()) {
                            return [];
                        }
                        const items_to_show = this.state.cachedMatches.slice(0, MAX_AUTOCOMPLETE_SHOWN);
                        const items = items_to_show.map((option, index) => ({
                            key: option.key,
                            cl: BuildClass({
                                option: true,
                                highlighted: this.state.highlightedIndex === index,
                            }),
                            children: [
                                {
                                    tag: 'span',
                                    cl: 'text',
                                    key: 'text',
                                    text: option.text,
                                },
                                ConditionalObject(option.extra, {
                                    tag: 'span',
                                    cl: 'extra',
                                    key: 'extra',
                                    text: option.extra ?? '',
                                }),
                            ],
                            onMouseDown: e => {
                                e.stopPropagation();
                                e.preventDefault();
                            },
                            onClick: e => {
                                e.stopPropagation();
                                e.preventDefault();
                                this.confirmSelection(true, option);
                            },
                        }));
                        const excess = Do(() => {
                            if (this.state.cachedMatches.length > MAX_AUTOCOMPLETE_SHOWN) {
                                return {
                                    cl: 'excess-count',
                                    key: 'excess-count',
                                    text: Do(() => {
                                        const count = this.state.cachedMatches.length -
                                            MAX_AUTOCOMPLETE_SHOWN;
                                        return `${count} more found`;
                                    }),
                                };
                            }
                            return undefined;
                        });
                        return _.flatten([items, excess]);
                    }),
                },
            ],
        };
    }
    getMatchingOptions(cb) {
        if (this.state.value.length === 0) {
            cb([]);
            return;
        }
        const value = this.state.value.toLowerCase().trim();
        const fuzzyValue = new RegExp(Array.from(value).join('.*?'));
        const prefixMatch = s => s.startsWith(value);
        const needleMatch = s => s.indexOf(value) !== -1;
        const fuzzyMatch = s => s.match(fuzzyValue) != null;
        const searchExtra = Boolean(this.props.searchExtra);
        this.callback = options => {
            const matches = _.compact(_.map(options, option => {
                const txt = option.text.toLowerCase().trim();
                const extra = option.extra.toLowerCase().trim();
                const match_type = Do(() => {
                    if (prefixMatch(txt)) {
                        return 1;
                    }
                    else if (needleMatch(txt)) {
                        return 2;
                    }
                    else if (fuzzyMatch(txt)) {
                        return 3;
                    }
                    else if (searchExtra && prefixMatch(extra)) {
                        return 4;
                    }
                    else if (searchExtra && needleMatch(extra)) {
                        return 5;
                    }
                    else if (searchExtra && fuzzyMatch(extra)) {
                        return 6;
                    }
                    return -1;
                });
                if (match_type === -1) {
                    return null;
                }
                return { option, match_type };
            }));
            return cb(fsmData(matches, {
                sort: match => `${match.match_type}-${match.option.text}-${match.option.extra ?? ''}`,
                map: match => match.option,
            }));
        };
        this.fetchOptions();
    }
    fetchOptions() {
        if (this.fetching) {
            return;
        }
        this.fetching = true;
        const fn = this.props.getOptions ?? Promise.resolve;
        new Promise(fn).then(x => {
            this.callback(x);
            this.fetching = false;
        });
    }
    moveSelection(ev, delta) {
        ev.preventDefault();
        ev.stopPropagation();
        this.setState(s => {
            let index = s.highlightedIndex ?? -1;
            index += delta;
            const maximum = Math.min(s.cachedMatches.length, MAX_AUTOCOMPLETE_SHOWN) - 1;
            if (index < 0) {
                index = 0;
            }
            if (index > maximum) {
                index = maximum;
            }
            return { highlightedIndex: index };
        });
    }
    confirmSelection(is_explicit, selected_item) {
        if (selected_item == null) {
            selected_item = this.state.cachedMatches[this.state.highlightedIndex];
        }
        if (is_explicit && this.state.cachedMatches.length === 1) {
            selected_item = this.state.cachedMatches[0];
        }
        if (selected_item == null) {
            return;
        }
        this.updateValue(selected_item.text, selected_item.key);
        if (is_explicit) {
            this.setState({ closed: true });
        }
    }
    updateValue(text, key) {
        this.setState({ value: text });
        this.props.onUpdate(text, key);
    }
}
//# sourceMappingURL=component-react-autocomplete.js.map